import { z } from 'zod';
import { idSchema, storageLocationSchema, unitKeySchema } from './common.js';
import { dateOnlySchema } from './plans.js';
import { SUGGESTION_SOURCES, storeDtoSchema, storeLocationSchema } from './stores.js';
/** Grocery lists, generation from plans, check-off with expiration learning, live sync — Phase 3 (PLAN §4.3–4.5, D10/D14). */
export const LIST_STATUSES = ['open', 'shopping', 'done', 'archived'];
export const listStatusSchema = z.enum(LIST_STATUSES);
export const LIST_ITEM_SOURCES = ['plan', 'manual', 'restock'];
export const listItemSourceSchema = z.enum(LIST_ITEM_SOURCES);
/**
 * Why a generated line could not be put into one unit (or rounded to
 * packages, or netted against the pantry): mirrors core `UNCONVERTED_REASONS`.
 */
export const UNCONVERTED_REASONS = [
    'missing_density',
    'missing_unit_weight',
    'incompatible_units',
];
export const unconvertedReasonSchema = z.enum(UNCONVERTED_REASONS);
export const groceryListDtoSchema = z.object({
    id: idSchema,
    name: z.string(),
    dateFrom: dateOnlySchema.nullable(),
    dateTo: dateOnlySchema.nullable(),
    storeId: idSchema.nullable(),
    status: listStatusSchema,
    itemCount: z.number().int().nonnegative(),
    checkedCount: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * What a non-observed location is based on: a greyed suggestion (PLAN §4.5,
 * `SUGGESTION_SOURCES`) or the household's own override (§9 #3b).
 */
export const GROCERY_LOCATION_SOURCES = [...SUGGESTION_SOURCES, 'household_override'];
export const groceryLocationSourceSchema = z.enum(GROCERY_LOCATION_SOURCES);
/**
 * An item's location at the list's store. `suggested` marks a greyed guess
 * (PLAN §4.5) and `source` names what it is based on; `source` is absent for
 * real (shared consensus) observations.
 */
export const groceryItemLocationSchema = storeLocationSchema.extend({
    suggested: z.boolean(),
    source: groceryLocationSourceSchema.optional(),
});
/** What we paid (`purchase`), the household's override, or the shared median. */
export const GROCERY_PRICE_SOURCES = ['purchase', 'household_override', 'shared'];
export const groceryPriceSourceSchema = z.enum(GROCERY_PRICE_SOURCES);
export const groceryItemDtoSchema = z.object({
    id: idSchema,
    listId: idSchema,
    ingredientId: idSchema,
    ingredientName: z.string(),
    productId: idSchema.nullable(),
    productLabel: z.string().nullable(),
    /**
     * One package of `productId` (for "buy 2 × 15 oz can"). Optional so older
     * cached payloads still parse; null without a product.
     */
    packageQty: z.number().positive().nullable().optional(),
    packageUnit: unitKeySchema.nullable().optional(),
    /** Net need after pantry subtraction, in `unit`. */
    neededQty: z.number().nonnegative().nullable(),
    unit: unitKeySchema,
    /** What to buy (rounded up to packages when a default product is known). */
    buyQty: z.number().nonnegative(),
    source: listItemSourceSchema,
    mealPlanIds: z.array(idSchema),
    /** "Tue dinner", "Thu lunch" — for the "for:" hint. */
    mealLabels: z.array(z.string()),
    note: z.string().nullable(),
    /**
     * Set when generation had to keep this ingredient on separate lines, could
     * not round it to packages, or skipped pantry stock because the units do
     * not convert. Null for clean lines and manual items.
     */
    unconvertedReason: unconvertedReasonSchema.nullable(),
    checkedAt: z.iso.datetime().nullable(),
    checkedByName: z.string().nullable(),
    purchaseId: idSchema.nullable(),
    /**
     * "I have this" (D35): set when the row was resolved from home instead of a
     * purchase. `checkedAt` is set too, so it still counts as checked.
     * Optional so older cached payloads still parse.
     */
    haveAt: z.iso.datetime().nullable().optional(),
    /** The pantry row the "have" created (null when not tracked, or since removed). */
    pantryItemId: idSchema.nullable().optional(),
    /** Location at the list's store (real or suggested), for sorting/display. */
    location: groceryItemLocationSchema.nullable(),
    priceCents: z.number().int().nonnegative().nullable(),
    /** Where `priceCents` comes from (optional so older cached payloads still parse). */
    priceSource: groceryPriceSourceSchema.nullable().optional(),
    photoMediaId: idSchema.nullable(),
    /** Stable sort key computed server-side for the selected store (PLAN §4.5). */
    sortKey: z.string(),
});
export const groceryListDetailSchema = z.object({
    list: groceryListDtoSchema,
    store: storeDtoSchema.nullable(),
    items: z.array(groceryItemDtoSchema),
    /** Departments in display order for the selected store (empty when no store). */
    departmentOrder: z.array(z.string()),
});
/** POST /api/v1/groceries — generate from plans in [dateFrom, dateTo] (PLAN §4.3); empty range = blank list */
export const createGroceryListRequestSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    dateFrom: dateOnlySchema.optional(),
    dateTo: dateOnlySchema.optional(),
    storeId: idSchema.nullable().optional(),
    /** Subtract usable pantry stock (default true). */
    subtractPantry: z.boolean().default(true),
    /** Add pantry items below their minimum as `restock` lines (default true). */
    includeRestock: z.boolean().default(true),
});
/** POST /api/v1/groceries/:id/regenerate — re-run generation, keeping manual items and check-offs */
export const regenerateGroceryListRequestSchema = createGroceryListRequestSchema.pick({
    dateFrom: true,
    dateTo: true,
    subtractPantry: true,
    includeRestock: true,
});
export const updateGroceryListRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    storeId: idSchema.nullable(),
    status: listStatusSchema,
})
    .partial();
export const groceryListQuerySchema = z.object({
    status: listStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
export const groceryListsResponseSchema = z.object({ lists: z.array(groceryListDtoSchema) });
export const addGroceryItemRequestSchema = z
    .object({
    ingredientId: idSchema.optional(),
    name: z.string().trim().min(1).max(120).optional(),
    productId: idSchema.optional(),
    qty: z.number().positive().max(100000),
    unit: unitKeySchema,
    note: z.string().trim().max(200).nullable().optional(),
})
    .refine((v) => Boolean(v.ingredientId) !== Boolean(v.name), 'exactly one of ingredientId or name');
export const updateGroceryItemRequestSchema = z
    .object({
    productId: idSchema.nullable(),
    buyQty: z.number().nonnegative().max(100000),
    unit: unitKeySchema,
    note: z.string().trim().max(200).nullable(),
})
    .partial();
// ------------------------------------------------------------ check-off --
/** GET /api/v1/groceries/:id/items/:itemId/checkoff-defaults?productId=&qty=&unit= (PLAN §4.4) */
export const checkoffDefaultsQuerySchema = z.object({
    productId: idSchema.optional(),
    qty: z.coerce.number().positive().optional(),
    unit: unitKeySchema.optional(),
});
export const EXPIRATION_RULES = [
    'history_store',
    'history_any',
    'store_product',
    'store_adjustment',
    'seed',
    'category',
    'unknown',
];
/** SYNC RULE: identical to `EXPIRATION_PRIOR_RULES` in `packages/core`. */
export const EXPIRATION_PRIOR_RULES = [
    'store_product',
    'store_adjustment',
    'seed',
    'category',
];
/**
 * How a `history_*` default was formed: the household's own observations
 * blended with a prior rule (PLAN §9 #5). Null for every other rule.
 */
export const expirationBasisSchema = z.object({
    observations: z.number().int().nonnegative(),
    priorRule: z.enum(EXPIRATION_PRIOR_RULES).nullable(),
});
/** How the check-off's expiry was chosen (stored on `purchase`). */
export const EXPIRES_ON_SOURCES = ['manual', 'accepted_default', 'unknown'];
export const checkoffDefaultsResponseSchema = z.object({
    expiresOn: dateOnlySchema.nullable(),
    /** Which rule produced the default — shown on the sheet so the user can correct it. */
    expirationRule: z.enum(EXPIRATION_RULES),
    /** Set when `expirationRule` is `history_*`: what the estimate is based on. */
    expirationBasis: expirationBasisSchema.nullable().optional(),
    storageLocation: storageLocationSchema,
    productId: idSchema.nullable(),
    priceCents: z.number().int().nullable(),
    priceSource: groceryPriceSourceSchema.nullable().optional(),
    location: groceryItemLocationSchema.nullable(),
    /** Prompts the sheet must show because the shared layer lacks the data. */
    needsPhoto: z.boolean(),
    needsPrice: z.boolean(),
    needsLocation: z.boolean(),
});
/** POST /api/v1/groceries/:id/items/:itemId/check — always qty + expiration; writes purchase + pantry_item (+ observations) */
export const checkOffRequestSchema = z.object({
    qty: z.number().positive().max(100000),
    unit: unitKeySchema,
    expiresOn: dateOnlySchema.nullable(),
    /**
     * True when the shopper typed or changed the date rather than accepting the
     * suggested default. Only edited dates count as observations for later
     * estimates; omitted means "accepted" (older clients).
     */
    expiresOnEdited: z.boolean().optional(),
    storageLocation: storageLocationSchema.default('pantry'),
    productId: idSchema.nullable().optional(),
    priceCents: z.number().int().min(0).max(100000000).nullable().optional(),
    location: storeLocationSchema.nullable().optional(),
    photoMediaId: idSchema.nullable().optional(),
    /**
     * Also save `priceCents` / `location` as the household's own value for this
     * product at this store (§9 #3b). The shared observation is still recorded.
     */
    saveOverride: z.boolean().optional(),
    /** Idempotency key from the client (multi-shopper races, retries). */
    clientKey: z.string().trim().max(64).optional(),
});
export const checkOffResponseSchema = z.object({
    item: groceryItemDtoSchema,
    purchaseId: idSchema,
    pantryItemId: idSchema,
});
/**
 * POST /api/v1/groceries/:id/items/:itemId/uncheck — the body is optional.
 * `expectedPurchaseId` is the check-off the client was looking at when the
 * shopper tapped "uncheck"; if the item has since been checked off again (by
 * another shopper, or a retried check) the server answers 409 `CHECK_CHANGED`
 * instead of undoing a purchase the client never saw (D14, multi-shopper).
 */
export const uncheckItemRequestSchema = z.object({
    expectedPurchaseId: idSchema.optional(),
});
// ------------------------------------------------------ "I have this" (D35) --
/**
 * POST /api/v1/groceries/:id/items/:itemId/have — resolve the row without a
 * purchase. Optionally adds the needed amount to the pantry; never writes a
 * purchase, price or location observation.
 */
export const haveItemRequestSchema = z.object({
    /** Also record what is at home as a pantry item (default true). */
    addToPantry: z.boolean().default(true),
    /** Defaults to the row's needed quantity (or the buy quantity). */
    qty: z.number().positive().max(100000).optional(),
    /** Defaults to the row's unit. */
    unit: unitKeySchema.optional(),
    /** Defaults to the usual place for the ingredient (PLAN §4.4). */
    storageLocation: storageLocationSchema.optional(),
    /** Omitted = the learned default (PLAN §4.4); null = unknown. */
    expiresOn: dateOnlySchema.nullable().optional(),
    /** Idempotency key from the client (retries, offline replay). */
    clientKey: z.string().trim().max(64).optional(),
});
export const haveItemResponseSchema = z.object({
    item: groceryItemDtoSchema,
    pantryItemId: idSchema.nullable(),
});
// -------------------------------------------------------- live sync (D14) --
/** GET /api/v1/groceries/:id/events — Server-Sent Events; `data` is one of these */
export const groceryEventSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('item.checked'), item: groceryItemDtoSchema, by: z.string() }),
    z.object({ type: z.literal('item.have'), item: groceryItemDtoSchema, by: z.string() }),
    z.object({ type: z.literal('item.unchecked'), item: groceryItemDtoSchema, by: z.string() }),
    z.object({ type: z.literal('item.added'), item: groceryItemDtoSchema }),
    z.object({ type: z.literal('item.updated'), item: groceryItemDtoSchema }),
    z.object({ type: z.literal('item.removed'), itemId: idSchema }),
    z.object({ type: z.literal('list.updated'), list: groceryListDtoSchema }),
    z.object({ type: z.literal('list.regenerated'), detail: groceryListDetailSchema }),
    z.object({ type: z.literal('list.deleted'), listId: idSchema }),
]);
/**
 * Every SSE event name the server emits (`event: <type>` frames). Clients must
 * subscribe to each one by name — named events never reach `onmessage` /
 * the generic `message` listener. Derived from the union so it cannot drift.
 */
export const groceryEventTypes = groceryEventSchema.options.map((o) => o.shape.type.value);
// ------------------------------------------------------ receipt scan (D34) --
/**
 * POST /api/v1/groceries/:id/receipt (multipart field `file`) reads a receipt
 * photo and proposes which receipt line paid for which unchecked row; nothing
 * is stored. POST /api/v1/groceries/:id/receipt/apply checks off the rows the
 * person confirmed, with the receipt's prices.
 */
export const RECEIPT_CONFIDENCES = ['high', 'medium', 'low'];
export const receiptConfidenceSchema = z.enum(RECEIPT_CONFIDENCES);
const cents = z.number().int().nonnegative();
export const receiptScanLineSchema = z.object({
    /** Position on the receipt (0-based). */
    index: z.number().int().nonnegative(),
    rawText: z.string(),
    normalisedText: z.string(),
    qty: z.number().nonnegative().nullable(),
    totalCents: cents,
    unitPriceCents: cents.nullable(),
    discountCents: cents.nullable(),
    /** The reader's food/non-food call, or false when the line is a fee, coupon or tax. */
    isFood: z.boolean().nullable(),
    suggestedItemId: idSchema.nullable(),
    /** Match score for `suggestedItemId` in [0, 1] (0 when there is none). */
    score: z.number().min(0).max(1),
    alternatives: z.array(z.object({ itemId: idSchema, score: z.number().min(0).max(1) })).max(3),
});
export const receiptScanResponseSchema = z.object({
    receipt: z.object({
        storeName: z.string().nullable(),
        storeAddress: z.string().nullable(),
        /** As printed: `YYYY-MM-DD`, or a date-time; null when unreadable. */
        purchasedAt: z.string().nullable(),
        currency: z.string(),
        subtotalCents: cents.nullable(),
        taxCents: cents.nullable(),
        totalCents: cents.nullable(),
        /** Sum of the lines net of discounts, for the totals check. */
        lineSumCents: z.number().int(),
        confidence: receiptConfidenceSchema,
    }),
    store: z.object({
        /** The store the receipt's name points at (prefers the list's store). */
        matchedStoreId: idSchema.nullable(),
        matchedStoreName: z.string().nullable(),
        listStoreId: idSchema.nullable(),
    }),
    lines: z.array(receiptScanLineSchema),
    /** Unchecked rows no line was matched to. */
    unmatchedItemIds: z.array(idSchema),
    warnings: z.array(z.string()),
});
export const receiptApplyRequestSchema = z
    .object({
    /** The receipt's purchase date; omitted/null = today. */
    purchasedAt: dateOnlySchema.nullable().optional(),
    /** Record the purchases at this store instead of the list's (the list itself is unchanged). */
    storeId: idSchema.nullable().optional(),
    matches: z
        .array(z.object({
        itemId: idSchema,
        qty: z.number().positive().max(100000),
        unit: unitKeySchema,
        priceCents: z.number().int().min(0).max(100000000),
        /** Omitted = the learned default, as on the check-off sheet. */
        expiresOn: dateOnlySchema.nullable().optional(),
        storageLocation: storageLocationSchema.optional(),
        clientKey: z.string().trim().max(64).optional(),
        /** The receipt line as printed; teaches the shared receipt-alias table (D34). */
        rawText: z.string().trim().max(200).optional(),
    }))
        .max(200),
    /** Receipt lines bought but not on the list: added as manual rows, then checked off. */
    newItems: z
        .array(z.object({
        rawText: z.string().trim().max(200),
        ingredientName: z.string().trim().min(1).max(120),
        qty: z.number().positive().max(100000),
        unit: unitKeySchema,
        priceCents: z.number().int().min(0).max(100000000),
        /** Idempotency key: a retried apply finds the row it already added instead of adding it twice. */
        clientKey: z.string().trim().max(64).optional(),
    }))
        .max(50)
        .default([]),
})
    .refine((v) => v.matches.length + v.newItems.length > 0, 'nothing to apply');
export const receiptApplyResponseSchema = z.object({
    checked: z.number().int().nonnegative(),
    added: z.number().int().nonnegative(),
    /** Every row the request touched, as it is now. */
    items: z.array(groceryItemDtoSchema),
    listStatus: listStatusSchema,
});
//# sourceMappingURL=groceries.js.map